export const formatDate = (date: Date | string | null): string | null => {
    if (!date) return null;

    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = d.toLocaleString('en-US', { month: 'short' });
    const year = d.getFullYear();

    return `${day} ${month} ${year}`;
};

// GST provision descriptions are generated server-side with a fixed
// "(DD/MM/YYYY - DD/MM/YYYY)" date range baked into the stored text (see
// express-server/services/gst-provision-service.js buildPeriodDescription) --
// that range can't itself be "live", so this re-renders it at display time
// using whichever date format function is currently in scope (e.g. the
// settings-provider's live formatDate), keeping it in sync with the user's
// date format preference without needing to touch stored data.
const GST_DESCRIPTION_DATE_RANGE = /\((\d{2})\/(\d{2})\/(\d{4}) - (\d{2})\/(\d{2})\/(\d{4})\)/;

export const reformatGstDescriptionDates = (
    description: string | null | undefined,
    formatDate: (date: Date | string) => string
): string => {
    if (!description) return description || '';
    const match = description.match(GST_DESCRIPTION_DATE_RANGE);
    if (!match) return description;

    const [full, d1, m1, y1, d2, m2, y2] = match;
    const start = new Date(Number(y1), Number(m1) - 1, Number(d1));
    const end = new Date(Number(y2), Number(m2) - 1, Number(d2));
    const replacement = `(${formatDate(start)} - ${formatDate(end)})`;

    return description.replace(full, replacement);
};

export const formatRelativeTime = (date: Date | string | null): string | null => {
    if (!date) return null;

    const now = new Date();
    const past = new Date(date);
    const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

    const intervals = [
        { label: 'year', seconds: 31536000 },
        { label: 'month', seconds: 2592000 },
        { label: 'week', seconds: 604800 },
        { label: 'day', seconds: 86400 },
        { label: 'hour', seconds: 3600 },
        { label: 'minute', seconds: 60 },
    ];

    for (const interval of intervals) {
        const count = Math.floor(diffInSeconds / interval.seconds);
        if (count >= 1) {
        return `${count} ${interval.label}${count > 1 ? 's' : ''} ago`;
        }
    }

    return 'Just now';
};

export const calculateDuration = (
    startDate: Date | string | null,
    endDate: Date | string | null
): string | null => {
    if (!startDate || !endDate) return null;

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    if (end < start) return 'Invalid date range';

    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    let days = end.getDate() - start.getDate();

    if (days < 0) {
        const previousMonth = new Date(end.getFullYear(), end.getMonth(), 0);
        days += previousMonth.getDate();
        months--;
    }

    if (months < 0) {
        months += 12;
        years--;
    }

    const parts = [];
    if (years > 0) parts.push(`${years} year${years > 1 ? 's' : ''}`);
    if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`);
    if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);

    return parts.length ? parts.join(' ') : '0 days';
};

export const getRemainingTimeText = (targetDate: Date | string | null): string => {
    if (!targetDate) return 'Invalid date';

    const now = new Date();
    const endDate = new Date(targetDate);

    if (isNaN(endDate.getTime())) return 'Invalid date';

    let diff = endDate.getTime() - now.getTime();
    if (diff <= 0) return 'Overdue';

    const msInMinute = 1000 * 60;
    const msInHour = msInMinute * 60;
    const msInDay = msInHour * 24;

    const days = Math.floor(diff / msInDay);
    diff %= msInDay;

    const hours = Math.floor(diff / msInHour);
    diff %= msInHour;

    const minutes = Math.floor(diff / msInMinute);

    const months = Math.floor(days / 30);
    const remainingDays = days % 30;

    const result: string[] = [];
    if (months > 0) result.push(`${months} month${months > 1 ? 's' : ''}`);
    if (remainingDays > 0) result.push(`${remainingDays} day${remainingDays > 1 ? 's' : ''}`);
    if (days === 0 && hours > 0) result.push(`${hours} hour${hours > 1 ? 's' : ''}`);
    if (days === 0 && hours === 0 && minutes > 0) result.push(`${minutes} minute${minutes > 1 ? 's' : ''}`);

    return result.join(' ') + ' remaining';
};
